import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { useMemo } from "react";
import { api } from "../../convex/_generated/api";
import {
  PRICING_PLANS,
  SERVER_LIMITS_BY_TIER,
  type SubscriptionTier,
  type SubscriptionStatus,
} from "@/lib/billing/plans";

// ─── Types ───────────────────────────────────────────────────────────────────

export type SubscriptionFeatureFlags = {
  /** Max number of organizations the user can own */
  serverLimit: number;
  /** Whether the user can create a new organization */
  canCreateOrg: boolean;
  /** Advanced analytics dashboard (Plus/Pro/Enterprise) */
  canViewAdvancedStats: boolean;
  /** Priority queue access (Pro/Enterprise) */
  canAccessPriorityQueue: boolean;
};

export type GracePeriodInfo = {
  /** True while the user is within the post-expiry grace window */
  isInGracePeriod: boolean;
  /** When the grace window closes (null when not in grace period) */
  graceEndsAt: number | null;
  /** Configured length of the grace window in days */
  gracePeriodDays: number;
};

export type UseSubscriptionResult = {
  /** True while Clerk auth and/or Convex queries are still loading */
  isLoading: boolean;

  /** Current plan tier ("free" | "starter" | "plus" | "pro" | "enterprise") */
  tier: SubscriptionTier;
  /** Current billing status */
  status: SubscriptionStatus;

  /** Whether the user may access premium-gated features right now */
  hasAccess: boolean;

  /** Whether the user is on a free trial */
  isTrialing: boolean;
  /** When the trial ends (ms epoch). null if not trialing */
  trialEndsAt: number | null;
  /** Whether the user has ever started a trial */
  hasUsedTrial: boolean;

  /** Max number of game servers/organizations the user can own */
  serverLimit: number;
  /** Full feature flag set */
  featureFlags: SubscriptionFeatureFlags;

  /** When the current billing period ends (ms epoch). null if not available */
  currentPeriodEnd: number | null;
  /** Whether auto-renew has been disabled (will expire at currentPeriodEnd) */
  cancelAtPeriodEnd: boolean;

  /** Grace period information */
  gracePeriod: GracePeriodInfo;

  /**
   * Human-readable plan name, e.g. "Founder Plus".
   * null when the user is on the free tier or has no matching plan.
   */
  planName: string | null;

  /** True if the user has an admin override (bypasses all billing checks) */
  isAdmin: boolean;

  /** True if the user has a lifetime access grant (bypasses all billing/subscription checks) */
  hasLifetimeAccess: boolean;

  /** True when the subscription has been manually paused (access suspended) */
  isPaused: boolean;

  /** Number of active (non-archived) organizations owned by the user */
  activeOrgCount: number;
  /** Number of archived organizations owned by the user */
  archivedOrgCount: number;
};

// ─── Defaults ────────────────────────────────────────────────────────────────

const FREE_FEATURE_FLAGS: SubscriptionFeatureFlags = {
  serverLimit: 0,
  canCreateOrg: false,
  canViewAdvancedStats: false,
  canAccessPriorityQueue: false,
};

const LOADING_RESULT: UseSubscriptionResult = {
  isLoading: true,
  tier: "free",
  status: "none",
  hasAccess: false,
  isTrialing: false,
  trialEndsAt: null,
  hasUsedTrial: false,
  serverLimit: 0,
  featureFlags: FREE_FEATURE_FLAGS,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  gracePeriod: {
    isInGracePeriod: false,
    graceEndsAt: null,
    gracePeriodDays: 3,
  },
  planName: null,
  isAdmin: false,
  hasLifetimeAccess: false,
  isPaused: false,
  activeOrgCount: 0,
  archivedOrgCount: 0,
};

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * useSubscription
 *
 * Returns the current user's full subscription state, including:
 * - Access control (`hasAccess`, `tier`, `status`)
 * - Feature flags (`featureFlags.canCreateOrg`, etc.)
 * - Billing period details (`currentPeriodEnd`, `cancelAtPeriodEnd`)
 * - Grace period state (`gracePeriod.isInGracePeriod`, etc.)
 * - Trial details (`isTrialing`, `trialEndsAt`)
 *
 * This is the single hook components should use for any subscription-aware UI.
 * It combines `getMyEntitlement` (access control + feature flags) and
 * `getMySummary` (detailed billing state) into one unified result.
 *
 * @example
 * ```tsx
 * function CreateServerButton() {
 *   const { featureFlags, isLoading } = useSubscription();
 *   if (isLoading || !featureFlags.canCreateOrg) return null;
 *   return <button>Create Server</button>;
 * }
 * ```
 */
export function useSubscription(): UseSubscriptionResult {
  const { isLoaded, isSignedIn } = useAuth();

  const entitlement = useQuery(
    api.billing.getMyEntitlement,
    isSignedIn ? {} : "skip"
  );

  const summary = useQuery(
    api.billing.getMySummary,
    isSignedIn ? {} : "skip"
  );

  return useMemo<UseSubscriptionResult>(() => {
    // Still loading Clerk auth
    if (!isLoaded) {
      return LOADING_RESULT;
    }

    // Not signed in or no entitlement data
    if (!isSignedIn || entitlement === null || summary === null) {
      return { ...LOADING_RESULT, isLoading: false };
    }

    // Queries still resolving
    if (entitlement === undefined || summary === undefined) {
      return LOADING_RESULT;
    }

    const tier = (entitlement.tier ?? "free") as SubscriptionTier;
    const status = (entitlement.status ?? "none") as SubscriptionStatus;
    const featureFlags = entitlement.featureFlags ?? FREE_FEATURE_FLAGS;

    const planDef = PRICING_PLANS.find((p) => p.id === tier) ?? null;
    const planName = planDef?.name ?? null;
    const serverLimit =
      featureFlags.serverLimit ??
      (entitlement.hasAccess ? (SERVER_LIMITS_BY_TIER[tier] ?? 0) : 0);

    return {
      isLoading: false,
      tier,
      status,
      hasAccess: entitlement.hasAccess,
      isTrialing: status === "trialing",
      trialEndsAt: summary.trialEndsAt ?? null,
      hasUsedTrial: summary.hasUsedTrial ?? false,
      serverLimit,
      featureFlags: {
        serverLimit,
        canCreateOrg: featureFlags.canCreateOrg ?? false,
        canViewAdvancedStats: featureFlags.canViewAdvancedStats ?? false,
        canAccessPriorityQueue: featureFlags.canAccessPriorityQueue ?? false,
      },
      currentPeriodEnd: summary.currentPeriodEnd ?? null,
      cancelAtPeriodEnd: summary.cancelAtPeriodEnd ?? false,
      gracePeriod: {
        isInGracePeriod: entitlement.isInGracePeriod ?? false,
        graceEndsAt: entitlement.graceEndsAt ?? null,
        gracePeriodDays: entitlement.gracePeriodDays ?? 3,
      },
      planName,
      isAdmin: summary.isAdmin ?? false,
      hasLifetimeAccess: (summary as any).hasLifetimeAccess ?? false,
      isPaused: (summary as any).isPaused ?? status === "paused",
      activeOrgCount: summary.usage?.activeOrgs ?? 0,
      archivedOrgCount: summary.usage?.archivedOrgs ?? 0,
    };
  }, [isLoaded, isSignedIn, entitlement, summary]);
}
